import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useStateSelector } from '../../../../../data/global/dataSelectors';
import { BooleanToggleType, DefaultApplicationModel, HeardAboutInfo, RetirementAccountType } from '@tradingblock/types';
import { DetailField, DetailFields } from '../../../../../components/basic/DetailFields';
import { getLocalDateFromTimezone } from '@tradingblock/api';
import { getDisplaySuffix } from '../../../../../utilities/accountManagement';

export const ApplicationSecureAccount: React.FunctionComponent<{
  heardAboutUsArray: HeardAboutInfo[];
}> = ({ heardAboutUsArray }) => {
  const application = useStateSelector(s => s.private.applications.currentItem.application);
  const {
    primaryAccountHolder,
    ownerUsername,
    retirementAccount,
    ownerCountry,
    flexibilityType,
    type,
    clearer,
    retirementAccountType,
    beneficiaryIraDecedentName,
    simpleIraPlanName,
    errorOnCreate,
  } = application || DefaultApplicationModel;
  const { firstName, middleName, lastName, suffix, email } =
    primaryAccountHolder || DefaultApplicationModel.primaryAccountHolder;

  function tryParseInt(str: string, defaultValue: number | null = null): number | null {
    const result = parseInt(str, 10);
    return isNaN(result) ? defaultValue : result;
  }

  const findRepDescription = useCallback(
    (hearAboutUsId: any, repId: any) => {
      const numberHearAboutUsId = tryParseInt(hearAboutUsId);
      const numberRepId = tryParseInt(repId);
      const heardAboutObject = heardAboutUsArray.find(obj => obj.heardAboutId === numberHearAboutUsId);
      let heardAboutUsDescription = '';
      let repCodeDescription = '';
      let officeCodesDescription;
      if (heardAboutObject) {
        if (heardAboutObject.representatives) {
          const repIdObject = heardAboutObject.representatives.find(rep => rep.repId === numberRepId);
          if (repIdObject) {
            repCodeDescription = repIdObject.description;
            officeCodesDescription = repIdObject.officeCodes;
          } else {
            officeCodesDescription = heardAboutObject.officeCodes;
          }
        }
        heardAboutUsDescription = heardAboutObject.description;
      }

      return [heardAboutUsDescription, repCodeDescription, officeCodesDescription];
    },
    [heardAboutUsArray]
  );

  const formatRetirementAccount = (toggleType: BooleanToggleType | null | undefined) => {
    if (!toggleType) {
      return 'No';
    }
    if (toggleType === BooleanToggleType.Yes) {
      return 'Yes';
    } else {
      return 'No';
    }
  };

  const formatFlexibilityType = (flexibilityType: string | null | undefined) => {
    if (!flexibilityType) {
      return '';
    }
    if (flexibilityType === 'CashOnly') {
      return 'Cash Only';
    } else {
      return 'Margin';
    }
  };

  const formatClearerType = (clearer: string | null | undefined) => {
    if (!clearer) {
      return '';
    }
    if (clearer === 'RQD') {
      return 'Zero Commission - RQD';
    } else {
      return 'Full Service - APEX';
    }
  };

  const fields = useMemo(() => {
    let fieldList: DetailField[] = [];
    if (application) {
      let desc = null;
      if (application.hearAboutUsId && application.repId)
        desc = findRepDescription(application.hearAboutUsId, application.repId);
      fieldList = [
        ...fieldList,

        {
          Value: () => (
            <>
              <div>
                <div className="fields-title mb-3">Secure Account</div>
                <div className="mb-3">
                  <div>Name</div>
                  <div className="mute">
                    {firstName} {middleName ? middleName : ''} {lastName} {getDisplaySuffix(suffix)}
                  </div>
                </div>
                <div className="mb-3">
                  <div>Email</div>
                  <div className="mute">{email}</div>
                </div>
              </div>
              <div className="mb-3">
                <div>Username</div>
                <div className="mute mb-3">{ownerUsername}</div>
              </div>
              {desc && (
                <>
                  <div className="mb-3">
                    <div>Heard About Us</div>
                    <div className="mute mb-3">{desc[0]}</div>
                  </div>
                  {desc[1] && (
                    <div className="mb-3">
                      <div>Rep Code</div>
                      <div className="mute mb-3">{desc[1]}</div>
                    </div>
                  )}
                  {desc[2] && (
                    <div className="mb-3">
                      <div>Office Codes</div>
                      <div className="mute mb-3">{desc[2].apex ? 'Apex: ' + desc[2].apex : ''}</div>
                      <div className="mute mb-3">{desc[2].rqd ? 'Rqd: ' + desc[2].rqd : ''}</div>
                    </div>
                  )}
                </>
              )}
            </>
          ),
        },
        {
          Value: () => (
            <div>
              <div className="fields-title mb-3">Account Type</div>
              <div className="mb-3">
                <div>Primary Account Holder Country</div>
                <div className="mute">{ownerCountry}</div>
              </div>
              <div className="mb-3">
                <div>Account Type</div>
                <div className="mute">{type}</div>
              </div>
              <div className="mb-3">
                <div> Retirement Account?</div>
                <div className="mute">{formatRetirementAccount(retirementAccount)}</div>
                {retirementAccountType === RetirementAccountType.Beneficiary && (
                  <div className="mb-3">
                    <div>Beneficiary IRA Decedent Name</div>
                    <div className="mute">{beneficiaryIraDecedentName}</div>
                  </div>
                )}
                {retirementAccountType === RetirementAccountType.Simple && (
                  <div className="mb-3">
                    <div>Simple IRA Plan Name</div>
                    <div className="mute">{simpleIraPlanName}</div>
                  </div>
                )}
              </div>
              <div className="mb-3">
                <div>Trading Flexibility</div>
                <div className="mute">{formatFlexibilityType(flexibilityType)}</div>
              </div>
              <div className="mb-3">
                <div>Clearer Type</div>
                <div className="mute">{formatClearerType(clearer)}</div>
              </div>
            </div>
          ),
        },
      ];
    }
    return fieldList;
  }, [application]);

  return (
    <div className="screen-body-section">
      {errorOnCreate && (
        <div className="warn">
          <div className="fields-title mb-3">
            {' '}
            <i className="fas fa-exclamation-triangle 3x"></i> Error on Account Creation
          </div>
          <div className="mb-3">
            <div style={{ fontWeight: 'bold' }}>Warning: This account is currently in an errored state.</div>
            <div>Please review the error details below and take the necessary steps to resolve the issue:</div>
            <div>{errorOnCreate.error}</div>
            <div className="mt-3">
              This error occurred on{' '}
              {getLocalDateFromTimezone(new Date(errorOnCreate.timeStamp)).format('MM/DD/YY h:mm:ss A')}.
            </div>
          </div>
        </div>
      )}
      <DetailFields fieldList={fields} valueAs="div" />
    </div>
  );
};
